import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

// Onboarding / "how it works" (mockup 2), built in the Home design language
// (emerald->teal gradient system) rather than the flat mockup skin. Mockup
// supplies the content + flow; Home supplies the aesthetic quality bar.
const FEATURES = [
  ['Legal Compliance', 'Your lease is checked against applicable Colorado laws.'],
  ['Risk Identification', 'Potential red flags and watch-outs are surfaced for you.'],
  ['Clear & Shareable', 'A plain-English report with a PDF you can save and share.'],
  ['Empowerment', 'Make informed decisions to protect your rights as a tenant.'],
  ['Peace of Mind', "Sign with confidence knowing you've reviewed the fine print."],
]

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
      <path d="M5 10.5l3.2 3.2L15 6.5" stroke="currentColor" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Onboarding() {
  const navigate = useNavigate()
  // The tier rides in from the website's pricing page (?plan=<tier>); the app
  // never re-asks. Default standard when the user arrives without one.
  const [params] = useSearchParams()
  const plan = params.get('plan') || 'standard'
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 dark:from-emerald-900 dark:via-green-900 dark:to-teal-900">
      <div className="relative mx-auto max-w-md px-6 py-12">
        <div className="pointer-events-none absolute -left-16 top-16 -z-10 h-56 w-56 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-16 bottom-24 -z-10 h-56 w-56 rounded-full bg-teal-400/10 blur-3xl" />

        <header className="animate-fade-in text-center">
          <h1 className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-4xl font-bold tracking-tight text-transparent dark:from-emerald-400 dark:to-teal-400">
            Welcome to LeaseCheck
          </h1>
          <div className="mt-5 space-y-3 text-slate-600 dark:text-slate-300">
            <p>Make sure your lease is compliant with local, state, and federal law.</p>
            <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
              Sign your lease with confidence.
            </p>
            <p>Understand your agreement fully — before you commit.</p>
          </div>
        </header>

        <section className="mt-11 animate-slide-in">
          <h2 className="text-center text-2xl font-bold text-slate-800 dark:text-slate-100">
            Key Features
          </h2>
          <ul className="mt-6 space-y-3">
            {FEATURES.map(([label, body]) => (
              <li
                key={label}
                className="flex gap-3 rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700"
              >
                <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow">
                  <CheckIcon />
                </span>
                <p className="text-slate-700 dark:text-slate-200">
                  <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                    {label}:
                  </span>{' '}
                  {body}
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10 animate-slide-in rounded-2xl bg-white/80 p-6 text-center shadow-md ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/70 dark:ring-slate-700">
          <p className="text-lg font-semibold italic text-slate-800 dark:text-slate-100">
            Ready to take control of your leasing experience?
          </p>
          <p className="mt-2 text-slate-600 dark:text-slate-300">
            Create your account and start your review today.
          </p>
        </section>

        <button
          onClick={() => navigate(`/login?mode=register&plan=${plan}`)}
          className="mt-8 w-full transform rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 py-4 text-lg font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 animate-slide-in"
        >
          Create Your Account
        </button>

        <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
          LeaseCheck screens Colorado residential leases for common legal and financial
          risks. It is not legal advice.
        </p>
      </div>
    </div>
  )
}

export default Onboarding
